import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { translate } from "../../services/translation.service";
import theme from "../../theme";

interface ICountdown {
  backgroundColor?: string;
  fontColor?: string;
}

interface ITimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  status_error: boolean;
  status_expired: boolean;
}

const Countdown = styled.div`
  && {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    background-color: ${(props: ICountdown): string =>
      props.backgroundColor || theme.colors.white};
    color: ${(props: ICountdown): string =>
      props.fontColor || theme.colors.black};

    article {
      display: flex;
      flex-direction: column;

      span {
        padding: 0 10px;
        text-align: center;

        &.value {
          border-right: 2px solid ${theme.colors.black};
        }
      }

      &:last-child {
        .value {
          border: 0;
        }
      }
    }

    .value {
      font-family: ${theme.fonts.subtitle};
      font-size: 30px;
      font-weight: 700;
    }
    .unit {
      font-size: 12px;
    }
  }
`;

const StyledImg = styled.img`
  && {
    padding: 10px;
  }
`;

interface IProps {
  data: {
    countdown_image?: {
      url?: string;
      alt?: string;
    };
    countdown_date: string;
    error_message?: string;
    expired_message?: string;
    background_color?: string;
    font_color?: string;
  };
}

// Add zero for one digit (for h, m, s)
// Remove days when it is null
const formatDate = (value: number): string => {
  if (value < 10) {
    return "0" + value;
  }

  return `${value}`;
};

// If the value is more than 1, add "s" to the word
const plurial = (value: number): string => (value > 1 ? "s" : "");

// Parse date
const countdownDateParsed = (prismicDate: string): Date => {
  const date =
    24 === prismicDate.length
      ? prismicDate.substring(0, 22) + ":" + prismicDate.substring(22, 24)
      : prismicDate;

  return new Date(date);
};

export default ({ data }: IProps): JSX.Element | null => {
  // Store time left in local state
  const [timeLeft, setTimeLeft] = useState<ITimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    status_error: false,
    status_expired: false,
  });

  const {
    days,
    hours,
    minutes,
    seconds,
    status_error,
    status_expired,
  } = timeLeft;

  const {
    countdown_image,
    countdown_date,
    error_message,
    expired_message,
    background_color,
    font_color,
  } = data;

  useEffect(() => {
    // ERROR
    if (!countdown_date) {
      return setTimeLeft(
        (tLeft: ITimeLeft): ITimeLeft => ({ ...tLeft, status_error: true })
      );
    }

    const countDownDate = countdownDateParsed(countdown_date).getTime();

    let update;

    // Update the count down every 1 second
    const loop = (): void => {
      // Get today's date and time
      const now = new Date().getTime();

      // Find the distance between now and the count down date
      const distance = countDownDate - now;

      // Time calculations for days, hours, minutes and seconds
      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        ),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
        status_error: isNaN(distance),
        status_expired: distance < 0,
      });

      // If the count down is finished or there is an error
      if (distance < 0 || isNaN(distance)) {
        clearInterval(update);
      }
    };

    update = setInterval(loop, 500);
    loop();

    return (): void => clearInterval(update);
  }, [countdown_date]);

  return (
    <Countdown
      className="countdown"
      backgroundColor={background_color}
      fontColor={font_color}
    >
      {status_error ? (
        translate(`${error_message}`)
      ) : status_expired ? (
        translate(`${expired_message}`)
      ) : (
        <>
          {countdown_image && (
            <StyledImg src={countdown_image.url} alt={countdown_image.alt} />
          )}
          <article className="days">
            <span className="value">{days}</span>
            <span className="unit">
              {translate("countdown.day") + plurial(days)}
            </span>
          </article>
          <article className="hours">
            <span className="value">{formatDate(hours)}</span>
            <span className="unit">
              {translate("countdown.hour") + plurial(hours)}
            </span>
          </article>
          <article className="minutes">
            <span className="value">{formatDate(minutes)}</span>
            <span className="unit">
              {translate("countdown.minute") + plurial(minutes)}
            </span>
          </article>
          <article className="seconds">
            <span className="value">{formatDate(seconds)}</span>
            <span className="unit">
              {translate("countdown.second") + plurial(seconds)}
            </span>
          </article>
        </>
      )}
    </Countdown>
  );
};
